import { Injectable } from '@angular/core';

import { ObjectBrowserFilter, ObjectBrowserFilterType, ProjectType, UserResponseData } from '@mirage-projects/cw-models';

import { UserService } from '../../abstract-classes/user.service';
import { AclControlService } from '../acl-permissions/acl-control.service';
import { ACL } from '../acl-permissions/acl.enum';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { UiConfigurationProjects } from '../ui-configuration/ui-configuration';
import { UiConfigurationService } from '../ui-configuration/ui-configuration.service';

/**
 * Business logic to get the projects filters for Project Browser
 */
@Injectable({ providedIn: 'root' })
export class ProjectListFilterService {
    private projectConfig: UiConfigurationProjects | undefined = this.uiConfiguration.uiConfiguration.projects;
    private filters: ObjectBrowserFilter[] = [];

    constructor(
        private aclControlService: AclControlService,
        private uiConfiguration: UiConfigurationService,
        private userService: UserService,
        private localStorageService: LocalStorageService
    ) {}

    /**
     * Initalize object browser filter data
     * @param disableTypeFilter optional param to disable the type filter(job-site only supports scan projects for now)
     * @returns
     */
    initFilters(disableTypeFilter = false): ObjectBrowserFilter[] {
        this.filters = [];

        if (this.hasToIncludeFilter('name')) {
            this.addSearchFilter();
        }

        if (this.hasToIncludeFilter('projectType')) {
            this.addProjectTypeFilter(disableTypeFilter);
        }

        if (this.hasToIncludeFilter('ordering')) {
            this.addOrderingFilter();
        }

        if (this.hasToIncludeFilter('onlyUser')) {
            this.addOnlyUserFilter();
        }

        if (this.hasToIncludeFilter('author')) {
            this.addAuthorFilter();
        }

        return this.filters;
    }

    private hasToIncludeFilter(filterType: string): boolean {
        return !this.projectConfig?.filters || this.projectConfig.filters.includes(filterType);
    }

    private addSearchFilter(): void {
        this.filters.push({
            type: ObjectBrowserFilterType.SEARCH,
            fieldName: 'name',
            queryField: 'search',
            placeholder: 'PROJECTS.TYPE_PROJECT_NAME',
            disable: false,
        });
    }

    private addProjectTypeFilter(disableTypeFilter: boolean): void {
        this.filters.push({
            type: ObjectBrowserFilterType.SELECT,
            fieldName: 'projectType',
            queryField: 'projectType',
            text: 'PROJECTS.SORT_BY',
            range: [
                {
                    value: '',
                    text: 'PROJECTS.ALL_TYPES',
                },
                {
                    value: 3,
                    text: 'PROJECTS.SCAN_TYPE',
                },
            ],
            selected: disableTypeFilter ? ProjectType.SCAN : '',
            value: disableTypeFilter ? ProjectType.SCAN : '',
            disable: disableTypeFilter,
        });
    }

    private addOrderingFilter(): void {
        const value = this.localStorageService.getItem('projectsOrder') ?? '-created_at';

        const projectsOrderFilter = {
            type: ObjectBrowserFilterType.SELECT,
            width: '16rem',
            fieldName: 'ordering',
            queryField: 'ordering',
            text: 'OBJECT_BROWSER.ORDER_BY',
            selected: value,
            value,
            range: [
                {
                    text: 'OBJECT_BROWSER.ORDER_BY_MOST_RECENTLY_OPENED',
                    value: '',
                },
                {
                    text: 'OBJECT_BROWSER.ORDER_BY_NAME_ASC',
                    value: 'name',
                },
                {
                    text: 'OBJECT_BROWSER.ORDER_BY_NAME_DESC',
                    value: '-name',
                },
                {
                    text: 'OBJECT_BROWSER.ORDER_BY_CREATED_AT_ASC',
                    value: 'created_at',
                },
                {
                    text: 'OBJECT_BROWSER.ORDER_BY_CREATED_AT_DESC',
                    value: '-created_at',
                },
                {
                    text: 'OBJECT_BROWSER.ORDER_BY_UPDATED_AT_ASC',
                    value: 'updated_at',
                },
                {
                    text: 'OBJECT_BROWSER.ORDER_BY_UPDATED_AT_DESC',
                    value: '-updated_at',
                },
            ],
            onChange: (data?: ObjectBrowserFilter): void => {
                // TODO: Create a service to manage user prefs
                this.localStorageService.setItem('projectsOrder', data?.value, true);

                const orderingFilter = this.filters.find((filter) => filter.fieldName === 'ordering');
                if (orderingFilter) {
                    orderingFilter.selected = data?.value;
                }
            },
            disable: false,
        };
        this.filters.push(projectsOrderFilter);
    }

    private addOnlyUserFilter(): void {
        this.filters.push({
            type: ObjectBrowserFilterType.TOGGLE,
            fieldName: 'onlyUser',
            queryField: 'onlyMyProjects',
            text: 'PROJECTS.SHOW_MY_PROJECTS',
            tooltip: 'PROJECTS.SHOW_MY_PROJECTS_TOOLTIP',
            trueValue: true,
            value: false,
            defaultValue: false,
            disableList: [
                {
                    field: 'author',
                    when: true,
                },
            ],
            disable: false,
        });
    }

    private addAuthorFilter(): void {
        const usersFilter: ObjectBrowserFilter = {
            items: [],
            shownItems: [],
            type: ObjectBrowserFilterType.AUTOCOMPLETE,
            fieldName: 'author',
            autocompleteSearchFn: (value: string | undefined): void => {
                if (!value || value.length < 3 || usersFilter.value !== value) {
                    usersFilter.shownItems = [];
                    usersFilter.value = '';
                }
                usersFilter.shownItems = (usersFilter?.items ?? [])
                    .sort((a, b) => a.userName.localeCompare(b.userName))
                    .filter((user) => {
                        return user.userName.toLowerCase().includes(value?.toLowerCase());
                    })
                    .map((item) => {
                        return { key: item.uid, value: item.userName };
                    });
            },
            hide: () => {
                return !(this.aclControlService.hasUserACL(ACL.USERS_GET) && this.aclControlService.hasUserACL(ACL.USERS_GET_LIST));
            },
            getText: (item: UserResponseData): string => {
                return item.userName;
            },
            placeholder: 'PROJECTS.BY_AUTHOR',
            queryField: 'author',
            value: '',
            dirtyField: '',
            onChange: function () {
                if (!this.value) {
                    return;
                }
                this.trueValue = this.value.uid;
            },
            disable: false,
        };

        this.filters.push(usersFilter);
        this.userService.getAll().subscribe((users) => {
            usersFilter.items = users;
        });
    }
}
